import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Bell, Check, CheckCheck } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) fetchUnread();
    const interval = setInterval(() => { if (user) fetchUnread(); }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchUnread = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/unread-count`);
      setUnread(data.count);
    } catch (e) { console.error('Fetch unread error:', e); }
  };

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API}/notifications?page_size=10`);
      setNotifications(data.items || []);
    } catch (e) { console.error('Fetch notifications error:', e); }
  };

  const toggleOpen = () => {
    if (!open) fetchNotifications();
    setOpen(!open);
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`, {});
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnread(prev => Math.max(0, prev - 1));
    } catch (e) { console.error('Mark read error:', e); }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {});
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnread(0);
    } catch (e) { console.error('Mark all read error:', e); }
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) { navigate(n.link); setOpen(false); }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return language === 'ar' ? 'الآن' : 'now';
    if (mins < 60) return language === 'ar' ? `${mins} د` : `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return language === 'ar' ? `${hrs} س` : `${hrs}h`;
    const days = Math.floor(hrs / 24);
    return language === 'ar' ? `${days} ي` : `${days}d`;
  };

  if (!user) return null;

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggleOpen} data-testid="notification-bell"
        className="relative p-2 text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted">
        <Bell size={20} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className={`absolute ${language === 'ar' ? 'left-0' : 'right-0'} top-full mt-2 w-80 bg-white border border-border rounded-xl shadow-xl z-50 overflow-hidden`} data-testid="notification-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-gray-50">
            <h3 className="font-heading font-bold text-sm text-primary">
              {language === 'ar' ? 'الإشعارات' : 'Notifications'}
            </h3>
            {unread > 0 && (
              <button onClick={markAllRead} data-testid="mark-all-read"
                className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 font-medium transition-colors">
                <CheckCheck size={14} />
                {language === 'ar' ? 'قراءة الكل' : 'Read all'}
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground" data-testid="no-notifications">
                {language === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
              </div>
            ) : (
              notifications.map((n) => (
                <div key={n.id} onClick={() => handleClick(n)} data-testid={`notification-item-${n.id}`}
                  className={`px-4 py-3 border-b border-border/50 cursor-pointer transition-colors hover:bg-gray-50 ${!n.read ? 'bg-accent/5' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!n.read ? 'font-semibold' : 'font-medium'} text-primary truncate`}>
                        {language === 'ar' ? n.title_ar : n.title_en}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {language === 'ar' ? n.message_ar : n.message_en}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{timeAgo(n.created_at)}</span>
                      {!n.read && <span className="w-2 h-2 bg-accent rounded-full" />}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
